import type { InternalAxiosRequestConfig } from "axios";

/**
 * Tenant print branding for the v2 stationery service.
 *
 * The service (stationery.aeronet.dev) resolves every tenant to its placeholder `default`
 * client_config ("Your Aviation Co Ltd") — it has no tenant→config mapping — but it merges a
 * render body's `config` over that default. The backend serves the tenant's own branding
 * (company block, AMO, logo as a data: URI) at GET /v6/stationery/branding, built from the
 * tenant's own data; this interceptor sends it with every `render` call.
 *
 * - Fetched once per page load and reused; a failed fetch is not cached, so the next print retries.
 * - A field the caller already put in `config` wins over the branding.
 * - Branding never blocks a print: if it can't be fetched, the body goes out unchanged.
 */
export type StationeryBranding = Record<string, string>;
export type BrandingFetcher = () => Promise<StationeryBranding>;

export function makeStationeryBrandingInterceptor(fetchBranding: BrandingFetcher) {
  let cached: Promise<StationeryBranding> | null = null;

  const branding = (): Promise<StationeryBranding> => {
    if (!cached) {
      cached = fetchBranding().catch(() => {
        cached = null;
        return {};
      });
    }
    return cached;
  };

  return async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
    const isRender = (config.method ?? "").toLowerCase() === "post" && /(^|\/)render\/?$/.test(config.url ?? "");
    const body = config.data;
    if (!isRender || !body || typeof body !== "object" || Array.isArray(body)) {
      return config;
    }
    const brand = await branding();
    if (Object.keys(brand).length === 0) {
      return config;
    }
    const callerConfig = body.config && typeof body.config === "object" ? body.config : {};
    config.data = { ...body, config: { ...brand, ...callerConfig } };
    return config;
  };
}
